import { useEffect, useState } from 'react'
import { api } from './api'
import { Dialogfenster } from './Dialogfenster'
import { fachlicherSatz } from './Fehlersatz'
import type { Spielschalterangabe } from './Spiel'
import { t } from './sprache'
import { Zahlenwahl } from './Zahlenwahl'
import type { Zahlenwahlangabe } from './Zahlenwahl'

/**
 * Mein Profil — was der Anwender für sich selbst einstellt (7.7, R920).
 *
 * Es geht auf, wenn er im Fuß der Seitenleiste auf seinen Namen klickt. Heute
 * stehen hier zwei Schalter: die Erfolgsmomente sowie Serie und Bestzeit.
 * *Meilensteine* kommen dazu, sobald es sie gibt — **ein Schalter, der
 * nichts bewirkt, wird nicht angeboten** (R280).
 *
 * Gespeichert wird mit dem Klick und nicht mit einem Knopf am Fuß: Es ist ein
 * Schalter, und ein Schalter, der erst nach „Speichern" schaltet, ist keiner.
 *
 * **Darüber steht seit dem 2026-10-06 die Zahlendarstellung** (7.2 Abschnitt
 * 43, R1314), die vorher in der Fußzeile stand. Ihren Stand hält die
 * Anwendung, weil jede Zahl auf dem Bildschirm davon abhängt; das Profil
 * zeigt ihn nur und meldet die Wahl zurück.
 */
export function Profil({
  aufSchliessen,
  zahlen,
}: {
  aufSchliessen: () => void
  zahlen?: Zahlenwahlangabe
}) {
  const [schalter, setSchalter] = useState<Spielschalterangabe | null>(null)
  /** Nur ein fachlicher Satz; eine Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004). */
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let gilt = true

    api
      .spielschalter()
      .then((s) => gilt && setSchalter(s))
      .catch((f) => gilt && setFehler(fachlicherSatz(f)))

    return () => {
      gilt = false
    }
  }, [])

  function schalte(aenderung: Partial<Spielschalterangabe>) {
    if (!schalter) {
      return
    }

    setFehler(null)
    api
      .spielschalterSetzen({ ...schalter, ...aenderung })
      .then(setSchalter)
      .catch((f) => setFehler(fachlicherSatz(f)))
  }

  return (
    <Dialogfenster titel={t('profil.titel')} weite="schmal" aufSchliessen={aufSchliessen}>
      {zahlen && <Zahlenwahl {...zahlen} />}
      <fieldset className="profilgruppe">
        <legend>{t('profil.spiel')}</legend>
        <p className="profilhinweis">{t('profil.spiel.hinweis')}</p>
        <label className="profilschalter">
          <input
            type="checkbox"
            checked={schalter?.erfolgsmomente ?? false}
            disabled={!schalter}
            onChange={(e) => schalte({ erfolgsmomente: e.target.checked })}
          />
          <span>{t('profil.spiel.erfolgsmomente')}</span>
        </label>
        <p className="profilhinweis">{t('profil.spiel.erfolgsmomente.hinweis')}</p>
        <label className="profilschalter">
          <input
            type="checkbox"
            checked={schalter?.serie ?? false}
            disabled={!schalter}
            onChange={(e) => schalte({ serie: e.target.checked })}
          />
          <span>{t('profil.spiel.serie')}</span>
        </label>
        <p className="profilhinweis">{t('profil.spiel.serie.hinweis')}</p>
        {fehler && (
          <p className="profilhinweis" role="alert">
            {fehler}
          </p>
        )}
      </fieldset>
    </Dialogfenster>
  )
}
